import { useState, useEffect } from 'react';
import axios from 'axios';
import { Calendar, Plus, X, Save, Loader2, ExternalLink, Info } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const MODES = [
  { key: 'MONTH', label: 'Month' },
  { key: 'WEEK', label: 'Week' },
  { key: 'AGENDA', label: 'Agenda' },
];

const TIMEZONES = [
  'Asia/Tokyo', 'Asia/Okinawa', 'Asia/Seoul', 'Asia/Shanghai', 'Asia/Singapore',
  'America/Los_Angeles', 'America/New_York', 'UTC',
];

export default function GoogleCalendar() {
  const [emails, setEmails] = useState([]);
  const [newEmail, setNewEmail] = useState('');
  const [timezone, setTimezone] = useState('Asia/Tokyo');
  const [mode, setMode] = useState('WEEK');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    axios.get(`${API}/api/admin/google-calendar`, { withCredentials: true })
      .then(res => {
        setEmails(res.data.emails || []);
        setTimezone(res.data.timezone || 'Asia/Tokyo');
        setMode(res.data.mode || 'WEEK');
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const addEmail = () => {
    const e = newEmail.trim();
    if (!e) return;
    if (emails.includes(e)) { setNewEmail(''); return; }
    setEmails([...emails, e]);
    setNewEmail('');
  };

  const removeEmail = (e) => setEmails(emails.filter(x => x !== e));

  const save = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/api/admin/google-calendar`,
        { emails, timezone, mode },
        { withCredentials: true });
      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
    } catch (err) {
      alert('Failed to save: ' + (err.response?.data?.detail || err.message));
    }
    setSaving(false);
  };

  const buildEmbedUrl = () => {
    if (emails.length === 0) return null;
    const params = new URLSearchParams();
    emails.forEach(e => params.append('src', e));
    params.set('ctz', timezone);
    params.set('mode', mode);
    params.set('showPrint', '0');
    params.set('showTabs', '1');
    params.set('showCalendars', '0');
    params.set('showTz', '0');
    return `https://calendar.google.com/calendar/embed?${params.toString()}`;
  };

  const embedUrl = buildEmbedUrl();

  if (loading) {
    return (
      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-8 flex items-center gap-2 text-muted-foreground">
        <Loader2 size={16} className="animate-spin" /> Loading calendar settings...
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 py-8 space-y-6" data-testid="google-calendar-panel">
      <div>
        <h2 className="font-heading text-2xl tracking-wider uppercase text-foreground mb-1 flex items-center gap-2">
          <Calendar size={22} className="text-brand" /> Google Calendar
        </h2>
        <p className="font-sans text-sm text-muted-foreground">
          Link one or more Google accounts to see your schedule here. Calendar must be set to <strong>public</strong> in Google Calendar sharing settings for embedding to work.
        </p>
      </div>

      {/* Setup help */}
      <div className="border border-brand/30 bg-brand/5 rounded-xl p-4 flex gap-3 text-sm font-sans">
        <Info size={18} className="text-brand shrink-0 mt-0.5" />
        <div className="space-y-1 text-foreground/80">
          <p className="font-medium text-foreground">How to make a Google Calendar embeddable:</p>
          <ol className="list-decimal ml-4 space-y-0.5 text-muted-foreground">
            <li>Open <a href="https://calendar.google.com" target="_blank" rel="noreferrer" className="text-brand underline inline-flex items-center gap-1">calendar.google.com <ExternalLink size={11} /></a> and hover the calendar under "My calendars"</li>
            <li>Click the 3-dot menu → <em>Settings and sharing</em></li>
            <li>Scroll to <em>Access permissions for events</em> → check <strong>"Make available to public"</strong> → <em>See all event details</em></li>
            <li>Copy the account email (or calendar ID) and paste it below</li>
          </ol>
        </div>
      </div>

      {/* Settings */}
      <div className="border border-border rounded-xl bg-card p-5 space-y-4">
        <label className="font-heading text-[11px] tracking-widest uppercase text-muted-foreground block">Linked Google Accounts / Calendar IDs</label>

        {/* Email list */}
        <div className="space-y-2">
          {emails.length === 0 && (
            <p className="font-sans text-sm text-muted-foreground italic">No calendars linked yet.</p>
          )}
          {emails.map(e => (
            <div key={e} className="flex items-center justify-between gap-3 bg-background border border-border rounded-lg px-3 py-2" data-testid={`linked-email-${e}`}>
              <span className="font-mono text-sm text-foreground truncate">{e}</span>
              <button
                onClick={() => removeEmail(e)}
                data-testid={`remove-email-${e}`}
                className="text-muted-foreground hover:text-brand transition-colors"
              >
                <X size={16} />
              </button>
            </div>
          ))}
        </div>

        {/* Add email */}
        <div className="flex gap-2">
          <input
            value={newEmail}
            onChange={e => setNewEmail(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && addEmail()}
            placeholder="info@okiescape.com"
            data-testid="new-email-input"
            className="flex-1 bg-background border border-border rounded-lg px-3 py-2 font-sans text-sm text-foreground focus:outline-none focus:border-brand placeholder-muted-foreground"
          />
          <button
            onClick={addEmail}
            data-testid="add-email-btn"
            className="flex items-center gap-1 font-heading text-xs tracking-widest uppercase bg-brand text-white px-4 py-2 rounded-lg hover:bg-brand-hover transition-colors"
          >
            <Plus size={14} /> Add
          </button>
        </div>

        {/* Time zone + View */}
        <div className="grid sm:grid-cols-2 gap-3 pt-2">
          <div>
            <label className="font-heading text-[11px] tracking-widest uppercase text-muted-foreground mb-1 block">Time Zone</label>
            <select
              value={timezone}
              onChange={e => setTimezone(e.target.value)}
              data-testid="timezone-select"
              className="w-full bg-background border border-border rounded-lg px-3 py-2 font-sans text-sm text-foreground focus:outline-none focus:border-brand"
            >
              {TIMEZONES.map(tz => <option key={tz} value={tz}>{tz}</option>)}
            </select>
          </div>
          <div>
            <label className="font-heading text-[11px] tracking-widest uppercase text-muted-foreground mb-1 block">Default View</label>
            <select
              value={mode}
              onChange={e => setMode(e.target.value)}
              data-testid="mode-select"
              className="w-full bg-background border border-border rounded-lg px-3 py-2 font-sans text-sm text-foreground focus:outline-none focus:border-brand"
            >
              {MODES.map(m => <option key={m.key} value={m.key}>{m.label}</option>)}
            </select>
          </div>
        </div>

        {/* Save */}
        <button
          onClick={save}
          disabled={saving}
          data-testid="save-calendar-btn"
          className="flex items-center gap-2 font-heading text-xs tracking-widest uppercase bg-brand text-white px-5 py-3 rounded-lg hover:bg-brand-hover transition-colors disabled:opacity-40"
        >
          {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
          {saved ? 'Saved!' : 'Save Settings'}
        </button>
      </div>

      {/* Embed */}
      {embedUrl ? (
        <div className="border border-border rounded-xl overflow-hidden bg-card" data-testid="calendar-embed">
          <iframe
            title="Google Calendar"
            src={embedUrl}
            width="100%"
            height="700"
            frameBorder="0"
            scrolling="no"
            style={{ border: 0 }}
          />
          <div className="px-4 py-2 border-t border-border flex items-center justify-between text-xs font-sans text-muted-foreground">
            <span>Showing {emails.length} calendar{emails.length === 1 ? '' : 's'}</span>
            <a href={embedUrl} target="_blank" rel="noreferrer" className="text-brand hover:underline inline-flex items-center gap-1">
              Open full size <ExternalLink size={11} />
            </a>
          </div>
        </div>
      ) : (
        <div className="border border-dashed border-border rounded-xl p-10 text-center text-muted-foreground font-sans text-sm">
          Add a Google account email above and click <strong>Save Settings</strong> to embed the calendar.
        </div>
      )}
    </div>
  );
}
